import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';
import useResizeObserver from '@react-hook/resize-observer';
import { useFlag } from '@unleash/proxy-client-react';
import cx from 'clsx';
import debounce from 'lodash.debounce';

import { ActionType } from '../../../../api/types';
import { ReactComponent as AddToKBAppsIcon } from '../../../../assets/icons/ask-ai-knowledgebase/add-to-kb-apps-icon.svg';
import { ReactComponent as AskAnExportIcon } from '../../../../assets/icons/ask-an-expert/ask-an-expert-icon.svg';
import { ReactComponent as DeepSearchIcon } from '../../../../assets/icons/chat/deep-search-icon.svg';
import { AppToggle } from '../../../../clients/unleash/app-toggle-names';
import { useAppSelector } from '../../../../redux/hooks/app-hooks';
import { useAddKnowledgeButton } from '../../../AskAiKnowledge/AddKnowledgeButton/useAddKnowledgeButton';
import { useAskAnExpertButton } from '../../../AskAnExpert/useAskAnExpertButton';
import { FollowUpAction } from '../../../Buttons/FollowUpAction';
import { useChatBoostModeButton } from '../../../Chat/Actions/useChatBoostMode';

import styles from './HeaderActionsList.module.scss';

const DEFINE_BUTTON_SIZE_DEBOUNCE_DELAY = 30;
const ICON_WIDTH = parseInt(styles.iconWidth, 10);
const ACTION_CONTENT_MARGIN = ICON_WIDTH / 2;
const ACTIONS_GAP = parseInt(styles.actionsGap, 10);
const WRAPPER_PADDING_X = parseInt(styles.actionsWrapperPaddingX, 10);

export type HeaderActionsListProps = {
    answersRenderedCount: number;
    className?: string;
};

export const HeaderActionsList: React.FC<HeaderActionsListProps> = ({ className, answersRenderedCount }) => {
    const shouldShowChatTab = useFlag(AppToggle.CHAT_TAB);
    const [iconOnly, setIconOnly] = useState(false);
    const actionType = useAppSelector((state) => state.question.action_type);
    const isSearchActionType = actionType === null || actionType === ActionType.SEARCH;

    const addKnowledgeButton = useAddKnowledgeButton();
    const askAnExpertButton = useAskAnExpertButton();
    const chatBoostModeButton = useChatBoostModeButton();

    const actionsListRef = useRef<HTMLDivElement | null>(null);

    // Define button configurations
    const actionsToRender = React.useMemo(() => {
        const actions = [
            {
                label: addKnowledgeButton.label,
                icon: AddToKBAppsIcon,
                isDisabled: addKnowledgeButton.disabled,
                onClick: addKnowledgeButton.onClick,
                enabled: isSearchActionType && addKnowledgeButton.enabled,
            },
            {
                label: askAnExpertButton.label,
                icon: AskAnExportIcon,
                isDisabled: askAnExpertButton.disabled,
                onClick: askAnExpertButton.onClick,
                enabled: shouldShowChatTab && answersRenderedCount > 0 && askAnExpertButton.enabled,
            },
            {
                label: chatBoostModeButton.label,
                icon: DeepSearchIcon,
                isDisabled: chatBoostModeButton.disabled,
                onClick: chatBoostModeButton.onClick,
                enabled: chatBoostModeButton.enabled,
            },
        ];

        return actions.filter((action) => action.enabled);
    }, [
        addKnowledgeButton.label,
        addKnowledgeButton.disabled,
        addKnowledgeButton.onClick,
        addKnowledgeButton.enabled,
        isSearchActionType,
        askAnExpertButton.label,
        askAnExpertButton.disabled,
        askAnExpertButton.onClick,
        askAnExpertButton.enabled,
        shouldShowChatTab,
        answersRenderedCount,
        chatBoostModeButton.label,
        chatBoostModeButton.disabled,
        chatBoostModeButton.onClick,
        chatBoostModeButton.enabled,
    ]);

    const defineButtonSize = useRef(
        debounce((wrapper: HTMLDivElement, actions: typeof actionsToRender) => {
            const wrapperWidth = wrapper.clientWidth - 2 * WRAPPER_PADDING_X;
            let totalWidth = actions.length > 1 ? (actions.length - 1) * ACTIONS_GAP : 0;

            // Create a hidden container to measure button sizes
            const hiddenContainer = document.createElement('div');
            hiddenContainer.style.position = 'absolute';
            hiddenContainer.style.visibility = 'hidden';
            hiddenContainer.style.pointerEvents = 'none';
            wrapper.appendChild(hiddenContainer);

            try {
                actions.forEach((item) => {
                    const label = document.createElement('span');
                    label.textContent = item.label;
                    label.classList.add(styles.label);

                    const button = document.createElement('button');
                    button.classList.add(styles.button);
                    button.appendChild(label);

                    hiddenContainer.appendChild(button);

                    totalWidth += button.clientWidth + ICON_WIDTH + ACTION_CONTENT_MARGIN;
                });

                setIconOnly(totalWidth > wrapperWidth);
            } finally {
                // clean up the hidden container
                if (hiddenContainer.parentNode) {
                    hiddenContainer.parentNode.removeChild(hiddenContainer);
                }
            }
        }, DEFINE_BUTTON_SIZE_DEBOUNCE_DELAY)
    ).current;

    // Handle resize events
    useResizeObserver(actionsListRef, (entry) => {
        const wrapper = entry.target;

        if (wrapper instanceof HTMLDivElement) {
            defineButtonSize(wrapper, actionsToRender);
        }
    });

    // Handle resize events
    useLayoutEffect(() => {
        const wrapper = actionsListRef.current;
        if (!wrapper) {
            return;
        }

        // Initial calculation
        defineButtonSize(wrapper, actionsToRender);

        // Cleanup
        return () => {
            if (typeof defineButtonSize.cancel === 'function') {
                defineButtonSize.cancel();
            }
        };
    }, [defineButtonSize, actionsToRender]);

    // If no actions to render, return null
    if (!actionsToRender.length) {
        return null;
    }

    return (
        <Box ref={actionsListRef} className={cx(styles.actions, className)}>
            {actionsToRender.map(({ label, icon, isDisabled, onClick }) => (
                <FollowUpAction
                    key={label}
                    iconOnly={iconOnly}
                    label={label}
                    icon={icon}
                    onClick={onClick}
                    isDisabled={isDisabled}
                    labelClassName={styles.label}
                    buttonClassName={styles.button}
                />
            ))}
        </Box>
    );
};
